import { TenantStatus } from '@today/types'
import { z } from 'zod'

// ─── Super-admin surface (/admin/tenants, TO-306) ─────────────────────────────

const tenantSlug = z
	.string()
	.min(1)
	.max(64)
	.regex(/^[a-z0-9-]+$/)

const tenantName = z.string().trim().min(1).max(128)

/** Upper bound of a tenant's default offer lifetime in days (TO-125). */
export const TENANT_DEFAULT_EXPIRATION_DAYS_MAX = 365

/** A tenant's default offer lifetime: whole days 1–365, or null for no expiry (TO-125). */
const tenantDefaultExpirationDays = z.int().min(1).max(TENANT_DEFAULT_EXPIRATION_DAYS_MAX).nullable()

/** `POST /admin/tenants` — the tenant plus the email of its first admin (REQ-004 / REQ-005). */
export const adminTenantCreateSchema = z
	.object({
		name: tenantName,
		slug: tenantSlug,
		// RFC 5321 caps the local part at 64; it becomes the first admin's display name.
		adminEmail: z
			.email()
			.max(320)
			.refine((email) => email.lastIndexOf('@') <= 64, 'The email local part must be at most 64 characters'),
	})
	.strict()

/**
 * `PATCH /admin/tenants/{tenantId}` — any non-empty subset of name / slug /
 * status (REQ-006). Strict, so a stray `isActive` is refused rather than
 * silently dropped.
 */
export const adminTenantUpdateSchema = z
	.object({
		name: tenantName.optional(),
		slug: tenantSlug.optional(),
		status: z.enum(TenantStatus).optional(),
		defaultExpirationDays: tenantDefaultExpirationDays.optional(),
	})
	.strict()
	.refine((patch) => Object.keys(patch).length > 0, {
		message: 'At least one of name, slug, status, defaultExpirationDays is required',
	})

export type AdminTenantCreateInput = z.infer<typeof adminTenantCreateSchema>
export type AdminTenantUpdateInput = z.infer<typeof adminTenantUpdateSchema>

// ─── Super-admin list paging + slug check (TO-628) ────────────────────────────

/** Upper bound of one `GET /admin/tenants` page (TO-628 BR-4). */
export const ADMIN_TENANT_LIST_MAX_LIMIT = 100

/**
 * Highest `page` accepted (TO-628 BR-4). Unbounded, a long digit string made the DB
 * offset `2e+37` or `Infinity`, which Postgres refuses as a bigint (22P02) — a 500
 * instead of an empty page. At this bound the largest offset is under 10^8.
 */
export const ADMIN_TENANT_LIST_MAX_PAGE = 1_000_000

/**
 * A positive base-10 integer as the query string carries it. Refuses `0`, `-1`,
 * `1.5`, `abc`, and `1,2` — which is how API Gateway delivers a repeated key — so
 * a bad page is a 422, never silently clamped.
 */
const positiveIntegerParam = z
	.string()
	.regex(/^[1-9][0-9]*$/, 'must be a positive integer')
	.transform(Number)

/** `GET /admin/tenants` query: `search`, plus `page` (1–1,000,000, default 1) and `limit` (1–100, default 20). */
export const adminTenantListQuerySchema = z.object({
	search: z.string().optional(),
	page: positiveIntegerParam
		.refine((page) => page <= ADMIN_TENANT_LIST_MAX_PAGE, `must be at most ${ADMIN_TENANT_LIST_MAX_PAGE}`)
		.optional()
		.transform((page) => page ?? 1),
	limit: positiveIntegerParam
		.refine((limit) => limit <= ADMIN_TENANT_LIST_MAX_LIMIT, `must be at most ${ADMIN_TENANT_LIST_MAX_LIMIT}`)
		.optional()
		.transform((limit) => limit ?? 20),
})

/** `GET /admin/tenants/slug-availability` query: the exact create-time slug rule, no trimming or case folding. */
export const adminTenantSlugQuerySchema = z.object({ slug: tenantSlug })
